import type { Key } from 'react';
import React from 'react';
import { atom, useSetAtom } from 'jotai';
import type { ConfigT } from '../../../../common/types/types';
import { SidePanelProvider } from '../../../../modernization-common/side-panel';
import type { ModernizedGenericAnnotation } from '../../../../modernization-common/side-panel/types';
import { LABEL_TYPE_CONFIG, TOOL_CONFIG } from '../../../labelling/components/toolbar/constants';
import { annotationLabelsAtom, frameCountAtom } from '../../atoms/annotation-atoms';
import { annotationsAtom, filteredAnnotationsAtom, importedMLAnnotationIdsAtom, mlAnnotationsAtom, pendingAnnotationAtom, selectedFilterAtom } from '../../atoms/side-panel-atoms';
import { focusedAnnotationIdAtom, selectedAnnotationIdsAtom, selectedAnnotationLabelAtom, showAnnotationDetailsAtom } from '../../atoms/tool-atoms';
import { LABELING_TITLES } from '../../constants';
import { AnnotationFilterContainer } from '../annotation-filter/annotation-filter';
import { DeleteAnnotationModalContainer } from './delete-annotation-modal-content/delete-annotation-modal-container';
import { useCreateRelationshipComponent } from './hooks/use-create-relationship-component';
import { useSidePanelHotkeys } from './hooks/use-side-panel-hotkeys';
import { useSidePanelQuestionnairePrefetch } from './hooks/use-side-panel-questionnaire-prefetch';
import { AdditionalOptionsLabelWrapper } from './additional-options-wrapper';
import { EditAnnotationsPanel } from './edit-annotations-panel';
import { InstanceSegmentationButton } from './instance-segmentation-button';
import { SidePanelContent } from './side-panel-content';
import { createSidePanelDispatchAtom } from './side-panel-dispatch';
export type SidePanelImageAnnotation = ModernizedGenericAnnotation;
export type RemainingItemsImageAnnotation = SidePanelImageAnnotation & {
  type: import('../../../../modernization-common/side-panel-m4/types').RemainingItemsType;
  message: string;
};
export const sidePanelDispatchAtom = createSidePanelDispatchAtom();
type PropsT = {
  classificationQuestionnaire?: {
    status: 'DONE' | 'PENDING' | 'OPTIONAL';
    Component: ({
      onClose
    }: {
      onClose?: () => void;
    }) => React.ReactNode;
  };
  labelingTitle?: string;
  onValidateAnswers?: (annotationId: string, answers: any) => Promise<any[]>;
  config?: ConfigT;
};
const SidePanelAdditionalOptions = ({
  labelingType
}: {
  labelingType?: string;
}) => <>{labelingType === LABELING_TITLES.IMAGE && <AdditionalOptionsLabelWrapper />}</>;
export const ImageSidePanel = ({
  classificationQuestionnaire,
  labelingTitle,
  onValidateAnswers,
  config
}: PropsT) => {
  const dispatch = useSetAtom(sidePanelDispatchAtom);
  useSidePanelHotkeys({
    dispatch
  });
  useSidePanelQuestionnairePrefetch();
  const additionalOptionsComponent = () => <SidePanelAdditionalOptions labelingType={labelingTitle} />;
  const createRelationshipComponent = useCreateRelationshipComponent();
  return <SidePanelProvider expandedAnnotationIdsAtom={atom<Set<Key>>(new Set<Key>())} expandedAnnotationSectionIdsAtom={atom<Set<Key>>(new Set<Key>())} visibilityModeAtom={atom({
    type: ('default' as const)
  })} numAnnotationFramesAtom={frameCountAtom} annotationsAtom={annotationsAtom} mlAnnotationsAtom={mlAnnotationsAtom} importedMLAnnotationIdsAtom={importedMLAnnotationIdsAtom} filteredAnnotationsAtom={filteredAnnotationsAtom} annotationLabelsAtom={annotationLabelsAtom} dispatchAtom={sidePanelDispatchAtom} pendingAnnotationAtom={pendingAnnotationAtom} toolConfig={{
    ...TOOL_CONFIG,
    ...LABEL_TYPE_CONFIG
  }} selectedAnnotationLabelAtom={selectedAnnotationLabelAtom} selectedAnnotationIdsAtom={selectedAnnotationIdsAtom} selectedFilterLabelAtom={selectedFilterAtom} focusedAnnotationIdAtom={focusedAnnotationIdAtom} taskQuestionnaireComponent={classificationQuestionnaire} showAnnotationDetailsAtom={showAnnotationDetailsAtom} relationshipComponent={undefined} editAnnotationComponent={({
    onClose
  }) => <EditAnnotationsPanel dispatchAtom={sidePanelDispatchAtom} onClose={onClose} onValidateAnswers={onValidateAnswers} config={config} />} annotationFilterComponent={() => <AnnotationFilterContainer />} labelListItemEnhancer={labelName => <InstanceSegmentationButton labelName={labelName} />} deleteAnnotationModalContentComponent={props => <DeleteAnnotationModalContainer {...props} />} additionalOptionsComponent={additionalOptionsComponent} createRelationshipComponent={createRelationshipComponent}>
      <SidePanelContent />
    </SidePanelProvider>;
};